import { createCollection } from "@tanstack/db";
import API from "../api";
import { capture } from "../instrument";
import { buildServerParams, matchesFilterSpec, serializeFilterSpec } from "./admissionFilter";

const INITIAL_PAGE_SIZE = 100;
const BACKFILL_PAGE_SIZE = 100;
const CATCHUP_PAGE_SIZE = 500;
const MAX_CACHED_VIEWS = 8;

// Cursors are opaque `"<submittedAt ISO>|<_id>"` strings minted by the server.
// We rebuild them locally when tracking the newest item so catchUp() picks up exactly
// where we left off without skipping siblings sharing a submittedAt millisecond.
const cursorTime = (cursor) => {
  if (!cursor) return 0;
  const [time] = String(cursor).split("|");
  return new Date(time).getTime() || 0;
};
const itemCursor = (item) => {
  const submittedAt = item.submittedAt || item.createdAt;
  if (!submittedAt) return null;
  return `${new Date(submittedAt).toISOString()}|${item._id}`;
};

const viewRegistry = new Map();

const bumpLru = (key, entry) => {
  entry.lastUsedAt = Date.now();
  viewRegistry.delete(key);
  viewRegistry.set(key, entry);
};

const evictLruIfNeeded = () => {
  while (viewRegistry.size > MAX_CACHED_VIEWS) {
    const oldestKey = viewRegistry.keys().next().value;
    const oldestEntry = viewRegistry.get(oldestKey);
    viewRegistry.delete(oldestKey);
    oldestEntry.collection.cleanup?.();
  }
};

const createView = (spec) => {
  let syncBegin = null;
  let syncWrite = null;
  let syncCommit = null;

  let oldestCursor = null;
  let newestCursor = null;
  let hasMoreOlder = true;
  let isLoadingOlder = false;
  let isCatchingUp = false;

  const updateCursors = (items) => {
    for (const item of items) {
      const cursor = itemCursor(item);
      if (!cursor) continue;
      const time = cursorTime(cursor);
      if (!newestCursor || time > cursorTime(newestCursor)) newestCursor = cursor;
      if (!oldestCursor || time < cursorTime(oldestCursor)) oldestCursor = cursor;
    }
  };

  const applyItems = (collection, items) => {
    if (items.length === 0 || !syncBegin) return;
    syncBegin({ immediate: true });
    for (const item of items) {
      const type = collection.has(item._id) ? "update" : "insert";
      syncWrite({ type, value: item });
    }
    syncCommit();
    updateCursors(items);
  };

  const applyDelete = (collection, id) => {
    if (!syncBegin) return;
    const existing = collection.get(id);
    if (!existing) return;
    syncBegin({ immediate: true });
    syncWrite({ type: "delete", value: existing });
    syncCommit();
  };

  const fetchPage = async (extraParams) => {
    const params = buildServerParams(spec);
    for (const [key, value] of Object.entries(extraParams || {})) params.set(key, value);
    const url = `/admin/admission?${params.toString()}`;
    const res = await API.get(url);
    if (!res.ok) throw new Error(res.code || "Failed to fetch admissions");
    return res.data;
  };

  const collection = createCollection({
    id: `admissions:${serializeFilterSpec(spec)}`,
    getKey: (item) => item._id,
    startSync: true,
    sync: {
      sync: async ({ collection: syncCollection, begin, write, commit, markReady }) => {
        syncBegin = begin;
        syncWrite = write;
        syncCommit = commit;
        try {
          const data = await fetchPage({ limit: String(INITIAL_PAGE_SIZE) });
          applyItems(syncCollection, data.admissions || []);
          if (data.nextCursor) oldestCursor = data.nextCursor;
          hasMoreOlder = data.hasMore ?? true;
        } catch (err) {
          capture(err);
        } finally {
          markReady();
        }
      },
    },
    utils: {
      writeUpsert: (item) => {
        const present = collection.has(item._id);
        const matches = matchesFilterSpec(item, spec);
        if (matches) {
          applyItems(collection, [item]);
        } else if (present) {
          applyDelete(collection, item._id);
        }
      },
      writeDelete: (id) => applyDelete(collection, id),
      loadOlder: async () => {
        if (isLoadingOlder || !hasMoreOlder || !oldestCursor) return;
        isLoadingOlder = true;
        try {
          const data = await fetchPage({ before: oldestCursor, limit: String(BACKFILL_PAGE_SIZE) });
          applyItems(collection, data.admissions || []);
          oldestCursor = data.nextCursor || oldestCursor;
          hasMoreOlder = data.hasMore ?? false;
        } catch (err) {
          capture(err);
        } finally {
          isLoadingOlder = false;
        }
      },
      catchUp: async () => {
        if (isCatchingUp) return;
        isCatchingUp = true;
        try {
          const data = newestCursor
            ? await fetchPage({ after: newestCursor, limit: String(CATCHUP_PAGE_SIZE) })
            : await fetchPage({ limit: String(INITIAL_PAGE_SIZE) });
          applyItems(collection, data.admissions || []);
        } catch (err) {
          capture(err);
        } finally {
          isCatchingUp = false;
        }
      },
    },
  });

  return collection;
};

export const getOrCreateAdmissionView = (spec) => {
  const key = serializeFilterSpec(spec);
  const existing = viewRegistry.get(key);
  if (existing) {
    bumpLru(key, existing);
    return existing.collection;
  }
  const collection = createView(spec);
  const entry = { collection, spec, lastUsedAt: Date.now() };
  viewRegistry.set(key, entry);
  evictLruIfNeeded();
  return collection;
};

export const broadcastAdmissionUpsert = (doc) => {
  for (const entry of viewRegistry.values()) {
    entry.collection.utils.writeUpsert(doc);
  }
};

export const broadcastAdmissionDelete = (id) => {
  for (const entry of viewRegistry.values()) {
    entry.collection.utils.writeDelete(id);
  }
};

export const catchUpAllViews = () => {
  for (const entry of viewRegistry.values()) {
    entry.collection.utils.catchUp();
  }
};

export const clearAllAdmissionViews = () => {
  for (const entry of viewRegistry.values()) {
    entry.collection.cleanup?.();
  }
  viewRegistry.clear();
};

export const admissionMatchesAnyCachedView = (doc) => {
  for (const entry of viewRegistry.values()) {
    if (matchesFilterSpec(doc, entry.spec)) return true;
  }
  return false;
};

export const findAdmissionInCachedViews = (id) => {
  if (!id) return null;
  for (const entry of viewRegistry.values()) {
    const hit = entry.collection.get(id);
    if (hit) return hit;
  }
  return null;
};
